import { Toaster } from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';


import { fetchPublicBranding } from '../api/auth.js';
import { detectSurface } from '../lib/subdomain.js';
import { useBrandingStore } from '../stores/branding-store.js';

/**
 * Root layout: ensures branding is fetched and applied as soon as we know
 * the org subdomain (so even the un-authed login page is themed). The
 * super-admin surface intentionally does NOT pull branding — its accent
 * is platform-fixed.
 */
export function RootRoute() {
  const surface = detectSurface();
  const apply = useBrandingStore((s) => s.apply);
  const clear = useBrandingStore((s) => s.clear);

  const brandingQuery = useQuery({
    queryKey: ['public-branding', surface.kind === 'org' ? surface.slug : null],
    queryFn: fetchPublicBranding,
    enabled: surface.kind === 'org',
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    if (surface.kind !== 'org') {
      clear();
      return;
    }
    if (brandingQuery.data) {
      apply({
        orgName: brandingQuery.data.name,
        orgSlug: brandingQuery.data.slug,
        primaryColorHex: brandingQuery.data.primaryColorHex,
        logoUrl: brandingQuery.data.logoUrl,
      });
    }
  }, [apply, brandingQuery.data, clear, surface.kind]);

  useEffect(() => {
    if (surface.kind === 'admin') {
      document.documentElement.dataset.surface = 'admin';
      document.title = 'SiteWise — admin';
    } else {
      document.documentElement.dataset.surface = 'org';
    }
  }, [surface.kind]);

  return (
    <>
      <Outlet />
      <Toaster />
    </>
  );
}
